import './mailCodeActivationPage.scss';

import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, trim } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import logo from '../../assets/logo-default.svg';
import vehicleSUVTopView from '../../assets/vehicle-suv-topview.png';
import { PhoneNumberInput } from '../../components/shared_new/PhoneNumberInput/PhoneNumberInput';
import useApiStore from '../../store/apiStore/useApiStore';
import useMailCodeStore from '../../store/mailCodeStore/useMailCodeStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';
import {
  emailValidator,
  nameValidator,
} from '../../utils/component/inputValidators';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();

  const { t } = useTranslation();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const { userExists } = useUserStore();

  const {
    computed: { applicantFirstName, applicantLastName },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    getDeliverPinParams,
    setMailCodeActivationState,
  } = useMailCodeStore();

  const form = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    } = values;

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    setMailCodeActivationState({
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
      userExistsException,
    });

    const deliverPinParams = getDeliverPinParams(DeliverPinLocation.SIGN_IN);

    await deliverPin(deliverPinParams);

    const { deliverPinSuccess } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigateWithCallback('/mail/pin-verify').then(() => {
        useUserStore.setState({
          userCreateSuccess: false,
          userAuthenticateSuccess: false,
          userUpdateSuccess: false,
        });
      });
    }
  };

  return (
    <div className="mail-code-activation-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.mailCodeActivation.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.vehicleSUVTopView')}
                src={vehicleSUVTopView}
              />
            </figure>
            <form
              data-testid={locator.pages.mailCodeActivation.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.mailCodeActivation.form.h1}>
                  {t('MailCodeActivationPage.title')}
                </h1>
                <h2 data-testid={locator.pages.mailCodeActivation.form.h2}>
                  {t('MailCodeActivationPage.subtitle')}
                </h2>

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.mailCodeActivation.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('firstNameValue', firstNameValidation)}
                        defaultValue={firstName || applicantFirstName}
                        error={!isEmpty(errors.firstNameValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.mailCodeActivation.form
                              .firstNameInput,
                        }}
                        label={t('MailCodeActivationPage.firstName')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.firstNameValue) && (
                        <span
                          data-testid={
                            locator.pages.mailCodeActivation.form
                              .firstNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.firstNameValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('lastNameValue', lastNameValidation)}
                        defaultValue={lastName || applicantLastName}
                        error={!isEmpty(errors.lastNameValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.mailCodeActivation.form.lastNameInput,
                        }}
                        label={t('MailCodeActivationPage.lastName')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.lastNameValue) && (
                        <span
                          data-testid={
                            locator.pages.mailCodeActivation.form
                              .lastNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.lastNameValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <PhoneNumberInput
                      form={form}
                      inputLabelKey={'MailCodeActivationPage.phone'}
                      inputName="primaryPhoneNumberValue"
                      inputProps={{
                        'data-testid':
                          locator.pages.mailCodeActivation.form.phoneInput,
                      }}
                      value={primaryPhoneNumber}
                    />

                    {!isEmpty(errors.primaryPhoneNumberValue) && (
                      <span
                        data-testid={
                          locator.pages.mailCodeActivation.form.phoneInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.primaryPhoneNumberValue.message)}
                      </span>
                    )}
                  </div>
                  <div>
                    <TextField
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      defaultValue={primaryEmailAddress}
                      error={!isEmpty(errors.primaryEmailAddressValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.mailCodeActivation.form.emailInput,
                      }}
                      label={t('MailCodeActivationPage.email')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.primaryEmailAddressValue) && (
                      <span
                        data-testid={
                          locator.pages.mailCodeActivation.form.emailInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.primaryEmailAddressValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.mailCodeActivation.form.submitButton
                  }
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('MailCodeActivationPage.continue')}
                </LoadingButton>
                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={
                            locator.pages.mailCodeActivation.form.termsLink
                          }
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={
                            locator.pages.mailCodeActivation.form.privacyLink
                          }
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'MailCodeActivationPage.termsPrivacy'}
                  />
                </p>
              </div>
              <footer
                data-testid={locator.pages.mailCodeActivation.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'MailCodeActivationPage';
